"use client";

import IconArrowInbox from "@ui/central-icons/icons/IconArrowInbox";
import IconWriting from "@ui/central-icons/icons/IconWriting";
import { useState, useEffect, useMemo, useRef } from "react";

import { useFormatter, useTranslations } from "next-intl";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

import { cn } from "@ui/lib/utils";
import { formatTime } from "@ui/lib/time";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { Button } from "../core/Button";
import { Link } from "../core/Link";
import type { ImageProps } from "next/image";

import { AudioRowPlayControl } from "../sound-effects/AudioRowPlayControl";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../core/Tooltip";
import { useAudioPlayer } from "./AudioPlayerContext";
import { WaveformBars, synthesizeWaveform } from "./WaveformBars";

interface SoundEffectExploreItem {
  id: string;
  title: string;
  thumbnailImageProps?: ImageProps;
  audioUrl?: string;
  duration?: string;
  durationSeconds?: number;
  isLoop?: boolean;
  downloadCount?: number;
  /** 3-4 word display title from LLM summarisation. Present only for XI items once cache is warm. Falls back to title. */
  summary?: string;
  /** Pre-computed visual waveform samples (e.g. from XI explore API). Used by showWaveform layout. */
  waveform?: number[];
}

interface CategoryExploreListLabels {
  headerCountSuffix: string;
  generateCtaDefaultLabel: string;
}

interface CategoryExploreListProps {
  items: SoundEffectExploreItem[];
  categoryName?: string;
  totalCount?: number;
  generateCtaHref?: string;
  generateCtaLabel?: string;
  generateCtaPosition?: number;
  generateCtaShowOnMobile?: boolean;
  generateFooterTitle?: string;
  generateFooterSubtitle?: string;
  onDownload?: (item: SoundEffectExploreItem) => void;
  onEditPrompt?: (item: SoundEffectExploreItem) => void;
  onGenerateSfx?: () => void;
  onPlay?: (item: SoundEffectExploreItem) => void;
  durationFormat?: "time" | "seconds";
  labels: CategoryExploreListLabels;
  /** Render duration as a dedicated right-side column instead of inline meta text. Defaults false (SFX layout). */
  showDurationColumn?: boolean;
  /** Render the precomputed waveform (from item.waveform) as a visual column. Defaults false (SFX layout). */
  showWaveform?: boolean;
  /** Render the download action with a visible "Download" label next to the icon. Defaults "icon" (SFX layout). */
  downloadButtonStyle?: "icon" | "icon-text";
}

type ExploreListColumnFlags = {
  showDownloadsColumn: boolean;
  showDurationColumn: boolean;
  showWaveform: boolean;
};

const EXPLORE_LIST_LG_GRID = {
  trackActions: "lg:tw-grid lg:tw-grid-cols-[minmax(0,3fr)_auto] lg:tw-items-center",
  trackDownloadsActions: "lg:tw-grid lg:tw-grid-cols-[minmax(0,3fr)_minmax(0,1fr)_auto] lg:tw-items-center",
  trackDurationActions: "lg:tw-grid lg:tw-grid-cols-[minmax(0,3fr)_4rem_auto] lg:tw-items-center",
  trackWaveformActions: "lg:tw-grid lg:tw-grid-cols-[minmax(0,3fr)_minmax(0,2fr)_auto] lg:tw-items-center",
  trackDownloadsDurationActions:
    "lg:tw-grid lg:tw-grid-cols-[minmax(0,3fr)_minmax(0,1fr)_4rem_auto] lg:tw-items-center",
  trackDownloadsWaveformActions:
    "lg:tw-grid lg:tw-grid-cols-[minmax(0,3fr)_minmax(0,1fr)_minmax(0,2fr)_auto] lg:tw-items-center",
  trackDurationWaveformActions: "lg:tw-grid lg:tw-grid-cols-[minmax(0,3fr)_4rem_minmax(0,2fr)_auto] lg:tw-items-center",
  trackDownloadsDurationWaveformActions:
    "lg:tw-grid lg:tw-grid-cols-[minmax(0,3fr)_minmax(0,1fr)_4rem_minmax(0,2fr)_auto] lg:tw-items-center",
} as const;

/** Shared lg grid so header and rows keep column alignment when optional cols (e.g. downloads) are present. */
function exploreListLgGridClassName({ showDownloadsColumn, showDurationColumn, showWaveform }: ExploreListColumnFlags) {
  if (showDownloadsColumn && showDurationColumn && showWaveform) {
    return EXPLORE_LIST_LG_GRID.trackDownloadsDurationWaveformActions;
  }
  if (showDownloadsColumn && showDurationColumn) {
    return EXPLORE_LIST_LG_GRID.trackDownloadsDurationActions;
  }
  if (showDownloadsColumn && showWaveform) {
    return EXPLORE_LIST_LG_GRID.trackDownloadsWaveformActions;
  }
  if (showDurationColumn && showWaveform) {
    return EXPLORE_LIST_LG_GRID.trackDurationWaveformActions;
  }
  if (showDownloadsColumn) {
    return EXPLORE_LIST_LG_GRID.trackDownloadsActions;
  }
  if (showDurationColumn) {
    return EXPLORE_LIST_LG_GRID.trackDurationActions;
  }
  if (showWaveform) {
    return EXPLORE_LIST_LG_GRID.trackWaveformActions;
  }
  return EXPLORE_LIST_LG_GRID.trackActions;
}

function GenerateCtaRow({
  href,
  label,
  onGenerateSfx,
  buttonLabel,
  showOnMobile,
}: {
  href: string;
  label: string;
  onGenerateSfx?: () => void;
  buttonLabel: string;
  showOnMobile?: boolean;
}) {
  return (
    <div
      className={cn(
        "tw-items-center tw-justify-between tw-border-b-2 tw-border-dotted tw-border-black/10 tw-py-3 tw-transition-colors last:tw-border-b-0",
        showOnMobile ? "tw-flex" : "tw-hidden lg:tw-flex",
        "tw-relative tw-cursor-pointer hover:tw-bg-[linear-gradient(90deg,_rgba(245,243,241,0)_0%,_#F5F3F1_8%,_#F5F3F1_92%,_rgba(245,243,241,0)_100%)]",
      )}
    >
      {/* Full-row clickable overlay */}
      {onGenerateSfx ? (
        <button className='tw-absolute tw-inset-0 tw-z-0' onClick={onGenerateSfx} aria-label={buttonLabel} />
      ) : (
        <a className='tw-absolute tw-inset-0 tw-z-0' href={href} aria-label={buttonLabel} />
      )}
      <div className='tw-flex tw-items-center tw-gap-4'>
        <div className='tw-flex tw-size-12 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-sm tw-border tw-border-white/10 tw-bg-gray-100'>
          <IconWriting className={cn(iconStrokeFix, "tw-size-5 tw-text-black")} />
        </div>
        <p className='tw-type-base tw-text-black'>{label}</p>
      </div>
      <div className='tw-relative tw-z-[1]'>
        {onGenerateSfx ? (
          <Button variant='elevated' size='md' onClick={onGenerateSfx}>
            {buttonLabel}
          </Button>
        ) : (
          <Link href={href} asButton buttonVariants={{ variant: "elevated", size: "md" }}>
            {buttonLabel}
          </Link>
        )}
      </div>
    </div>
  );
}

function GenerateFooter({
  href,
  title,
  subtitle,
  onGenerateSfx,
  buttonLabel,
}: {
  href: string;
  title: string;
  subtitle?: string;
  onGenerateSfx?: () => void;
  buttonLabel: string;
}) {
  return (
    <div className='tw-mt-12 tw-flex tw-flex-col tw-items-center tw-gap-6 tw-text-center'>
      <div className='tw-flex tw-flex-col tw-items-center tw-gap-4'>
        <p className='tw-type-xl tw-text-black tw-max-w-sm'>{title}</p>
        {subtitle && <p className='tw-type-sm tw-text-gray-500 tw-max-w-sm'>{subtitle}</p>}
      </div>
      {onGenerateSfx ? (
        <Button variant='elevated' size='lg' onClick={onGenerateSfx}>
          <IconWriting className={cn(iconStrokeFix, "tw-size-4")} />
          {buttonLabel}
        </Button>
      ) : (
        <Link href={href} asButton buttonVariants={{ variant: "elevated", size: "lg" }}>
          <IconWriting className={cn(iconStrokeFix, "tw-size-4")} />
          {buttonLabel}
        </Link>
      )}
    </div>
  );
}

function AudioRowWaveform({
  item,
  itemPosition,
  fetchedDurationSeconds,
  onPlay,
}: {
  item: SoundEffectExploreItem;
  itemPosition: number;
  fetchedDurationSeconds: number | null;
  onPlay?: (item: SoundEffectExploreItem) => void;
}) {
  const { activeId, currentTime, duration, playFrom } = useAudioPlayer();
  const startedFromWaveformRef = useRef(false);
  const isActiveTrack = activeId === item.id;
  const knownDuration = item.durationSeconds ?? fetchedDurationSeconds;
  const effectiveDuration = isActiveTrack ? duration || knownDuration || 0 : (knownDuration ?? 0);
  const waveformProgress = isActiveTrack && effectiveDuration > 0 ? currentTime / effectiveDuration : 0;
  const waveformData = useMemo(
    () => (item.waveform && item.waveform.length > 0 ? item.waveform : synthesizeWaveform(item.id)),
    [item.id, item.waveform],
  );

  useEffect(() => {
    if (!isActiveTrack) startedFromWaveformRef.current = false;
  }, [isActiveTrack]);

  const handleSeekPercent = (nextProgress: number) => {
    if (!item.audioUrl) return;

    if (effectiveDuration <= 0) {
      if (!isActiveTrack && !startedFromWaveformRef.current) {
        startedFromWaveformRef.current = true;
        onPlay?.(item);
        playFrom({ id: item.id, audioUrl: item.audioUrl }, 0);
      }
      return;
    }

    const nextTime = nextProgress * effectiveDuration;
    if (isActiveTrack || startedFromWaveformRef.current) {
      playFrom(
        {
          id: item.id,
          audioUrl: item.audioUrl,
          knownDuration: knownDuration ?? effectiveDuration,
        },
        nextTime,
      );
      return;
    }

    startedFromWaveformRef.current = true;
    onPlay?.(item);
    playFrom(
      {
        id: item.id,
        audioUrl: item.audioUrl,
        knownDuration: knownDuration ?? effectiveDuration,
      },
      nextTime,
    );
  };

  return (
    <div className='tw-relative tw-z-[1] tw-hidden tw-min-w-0 tw-items-center tw-overflow-hidden tw-pr-6 lg:tw-flex'>
      <WaveformBars
        ariaLabel={`Seek ${item.title}`}
        data={waveformData}
        disabled={!item.audioUrl}
        onInteractionStart={({ inputMethod, progress }) => {
          captureEvent(EventName.mw_music_category_waveform_seeked, {
            audio_list_element_id: item.id,
            audio_list_element_position: String(itemPosition),
            input_method: inputMethod,
            seek_percent: Math.round(progress * 100),
          });
        }}
        onSeekPercent={handleSeekPercent}
        progress={waveformProgress}
      />
    </div>
  );
}

function AudioRow({
  item,
  itemPosition,
  showDownloadsColumn,
  onDownload,
  onEditPrompt,
  onPlay,
  durationFormat = "time",
  showDurationColumn = false,
  showWaveform = false,
  downloadButtonStyle = "icon",
}: {
  item: SoundEffectExploreItem;
  itemPosition: number;
  showDownloadsColumn: boolean;
  onDownload?: (item: SoundEffectExploreItem) => void;
  onEditPrompt?: (item: SoundEffectExploreItem) => void;
  onPlay?: (item: SoundEffectExploreItem) => void;
  durationFormat?: "time" | "seconds";
  showDurationColumn?: boolean;
  showWaveform?: boolean;
  downloadButtonStyle?: "icon" | "icon-text";
}) {
  const t = useTranslations("ClientKeys.Globals");
  const format = useFormatter();

  const rowRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [fetchedDuration, setFetchedDuration] = useState<string | null>(null);
  const [fetchedDurationSeconds, setFetchedDurationSeconds] = useState<number | null>(null);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "50px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    if ((item.duration && item.durationSeconds != null) || !item.audioUrl) return;

    const audio = new Audio();
    audio.preload = "metadata";

    const onLoadedMetadata = () => {
      if (isFinite(audio.duration)) {
        setFetchedDuration(formatTime(audio.duration));
        setFetchedDurationSeconds(audio.duration);
      }
    };

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.src = item.audioUrl;

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.src = "";
    };
  }, [isVisible, item.duration, item.durationSeconds, item.audioUrl]);

  const displayDuration = (() => {
    if (durationFormat === "seconds") {
      const secs = item.durationSeconds ?? fetchedDurationSeconds;
      return secs != null ? `${secs.toFixed(1)}s` : null;
    }
    const time = item.duration || fetchedDuration;
    if (time) return time;
    return showDurationColumn ? null : "00:00";
  })();
  const metaParts: string[] = [];
  if (!showDurationColumn && displayDuration) metaParts.push(displayDuration);
  if (item.isLoop != null) metaParts.push(item.isLoop ? t("loop") : t("no_loop"));
  const metaText = metaParts.join(" \u2022 ");
  const columnFlags = { showDownloadsColumn, showDurationColumn, showWaveform };

  return (
    <div
      ref={rowRef}
      className={cn(
        "[&:hover:not(:has([data-action-buttons]:hover))]:tw-bg-[linear-gradient(90deg,_rgba(245,243,241,0)_0%,_#F5F3F1_8%,_#F5F3F1_92%,_rgba(245,243,241,0)_100%)] tw-group tw-relative tw-flex tw-items-center tw-border-b-2 tw-border-dotted tw-border-black/10 tw-py-3 tw-transition-colors last:tw-border-b-0",
        exploreListLgGridClassName(columnFlags),
        item.audioUrl && "tw-cursor-pointer",
      )}
    >
      {/* Thumbnail + Title + Meta */}
      <div className='lg:tw-pr-10 tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-4 lg:tw-min-w-0 lg:tw-flex-none'>
        <AudioRowPlayControl
          id={item.id}
          audioUrl={item.audioUrl}
          thumbnailImageProps={item.thumbnailImageProps}
          title={item.title}
          onPlay={onPlay ? () => onPlay(item) : undefined}
          knownDuration={item.durationSeconds}
        />
        <div className='tw-flex tw-grow tw-gap-4 tw-min-w-0 tw-flex-col tw-justify-center'>
          <p className='tw-type-base tw-leading-[1.2] tw-text-black'>
            <span className='tw-line-clamp-2 lg:tw-line-clamp-none lg:tw-truncate tw-text-pretty'>
              {item.summary || item.title}
            </span>
          </p>
          {metaText ? (
            <p className='tw-type-sm tw-leading-[1.2] tw-text-gray-500'>{metaText}</p>
          ) : !showDurationColumn && item.audioUrl ? (
            <p className='tw-type-sm tw-leading-[1.2] tw-text-gray-500'>
              <span className='tw-inline-block tw-h-3.5 tw-w-8 tw-animate-pulse tw-rounded tw-bg-gray-200 tw-align-middle' />
            </p>
          ) : null}
        </div>
      </div>

      {/* Downloads column — hidden on mobile, hidden entirely when no item has a count */}
      {showDownloadsColumn && (
        <div className='tw-hidden tw-min-w-0 tw-items-center tw-pr-[60px] lg:tw-flex'>
          <p className='tw-type-base tw-text-black'>
            {item.downloadCount != null ? format.number(item.downloadCount) : "\u2014"}
          </p>
        </div>
      )}

      {/* Duration column (opt-in) — sits to the right of the title, before the waveform */}
      {showDurationColumn && (
        <div className='tw-hidden tw-min-w-0 tw-items-center tw-justify-end tw-pr-6 lg:tw-flex'>
          <p className='tw-type-sm tw-tabular-nums tw-text-gray-500'>
            {displayDuration ?? (
              <span className='tw-inline-block tw-h-3.5 tw-w-8 tw-animate-pulse tw-rounded tw-bg-gray-200 tw-align-middle' />
            )}
          </p>
        </div>
      )}

      {/* Waveform column (opt-in) — fills proportionally while the row's track is active */}
      {showWaveform && (
        <AudioRowWaveform
          item={item}
          itemPosition={itemPosition}
          fetchedDurationSeconds={fetchedDurationSeconds}
          onPlay={onPlay}
        />
      )}

      {/* Action buttons — sit above the invisible row overlay button */}
      <div data-action-buttons className='tw-z-[1] tw-relative tw-flex tw-shrink-0 tw-items-center'>
        {onEditPrompt ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost-warm'
                  size='pill'
                  aria-label={t("edit_prompt_label")}
                  icon={<IconWriting className={cn(iconStrokeFix, "tw-size-5")} />}
                  onClick={onEditPrompt ? () => onEditPrompt(item) : undefined}
                />
              </TooltipTrigger>
              <TooltipContent variant='dark-new'>{t("edit_prompt_label")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
        {onDownload ? (
          downloadButtonStyle === "icon-text" ? (
            <>
              <Button
                variant='ghost-warm'
                size='pill'
                className='sm:tw-hidden'
                aria-label={t("download")}
                icon={<IconArrowInbox className={cn(iconStrokeFix, "tw-size-5")} />}
                onClick={() => onDownload(item)}
              />
              <Button
                variant='ghost-warm'
                size='md'
                className='tw-hidden sm:tw-inline-flex'
                icon={<IconArrowInbox className={cn(iconStrokeFix, "tw-size-5")} />}
                iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
                onClick={() => onDownload(item)}
              >
                {t("download")}
              </Button>
            </>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='ghost-warm'
                    size='pill'
                    aria-label={t("download")}
                    icon={<IconArrowInbox className={cn(iconStrokeFix, "tw-size-5")} />}
                    onClick={() => onDownload(item)}
                  />
                </TooltipTrigger>
                <TooltipContent variant='dark-new'>{t("download")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        ) : null}
      </div>
    </div>
  );
}

const CategoryExploreList = ({
  items,
  categoryName,
  totalCount,
  generateCtaHref,
  generateCtaLabel,
  generateCtaPosition = 3,
  generateCtaShowOnMobile,
  generateFooterTitle,
  generateFooterSubtitle,
  onDownload,
  onEditPrompt,
  onGenerateSfx,
  onPlay,
  durationFormat,
  labels,
  showDurationColumn = false,
  showWaveform = false,
  downloadButtonStyle = "icon",
}: CategoryExploreListProps) => {
  const tGlobals = useTranslations("ClientKeys.Globals");
  const tList = useTranslations("ClientKeys.CategoryExploreList");

  const count = totalCount ?? items.length;
  const headerText = [String(count), categoryName, labels.headerCountSuffix].filter(Boolean).join(" ");
  const showDownloadsColumn = items.some(item => item.downloadCount != null);
  const columnFlags = { showDownloadsColumn, showDurationColumn, showWaveform };

  // Build rows, inserting the CTA row at generateCtaPosition (or at end if beyond bounds)
  const ctaLabel = generateCtaLabel ?? labels.generateCtaDefaultLabel;
  const ctaPosition = generateCtaHref ? generateCtaPosition : -1;

  const rows = items.flatMap((item, index) => {
    const audioRow = (
      <AudioRow
        key={item.id}
        item={item}
        itemPosition={index + 1}
        showDownloadsColumn={showDownloadsColumn}
        onDownload={onDownload}
        onEditPrompt={onEditPrompt}
        onPlay={onPlay}
        durationFormat={durationFormat}
        showDurationColumn={showDurationColumn}
        showWaveform={showWaveform}
        downloadButtonStyle={downloadButtonStyle}
      />
    );
    if (index === ctaPosition) {
      return [
        <GenerateCtaRow
          key='generate-cta'
          href={generateCtaHref!}
          label={ctaLabel}
          onGenerateSfx={onGenerateSfx}
          buttonLabel={tList("generate_cta_button")}
          showOnMobile={generateCtaShowOnMobile}
        />,
        audioRow,
      ];
    }
    return [audioRow];
  });

  if (generateCtaHref && ctaPosition >= items.length) {
    rows.push(
      <GenerateCtaRow
        key='generate-cta'
        href={generateCtaHref}
        label={ctaLabel}
        onGenerateSfx={onGenerateSfx}
        buttonLabel={tList("generate_cta_button")}
        showOnMobile={generateCtaShowOnMobile}
      />,
    );
  }

  return (
    <div className='tw-flex tw-flex-col'>
      {/* Header — structure exactly mirrors AudioRow columns */}
      <div
        className={cn(
          "tw-text-gray-500 tw-flex tw-items-center tw-pt-2 tw-pb-2 lg:tw-pb-4",
          exploreListLgGridClassName(columnFlags),
        )}
      >
        {/* Col 1: mirrors left col of AudioRow */}
        <div className='lg:tw-pr-10 tw-flex tw-min-w-0 tw-flex-1 tw-items-center lg:tw-min-w-0 lg:tw-flex-none'>
          <p className='tw-type-sm tw-min-w-0'>{headerText}</p>
        </div>
        {/* Col 2: mirrors downloads col of AudioRow */}
        {showDownloadsColumn && (
          <div className='tw-hidden tw-min-w-0 tw-items-center tw-pr-[60px] lg:tw-flex'>
            <p className='tw-type-sm'>{tGlobals("downloads")}</p>
          </div>
        )}
        {/* Col 3: invisible spacer mirrors the duration column */}
        {showDurationColumn && (
          <div className='tw-invisible tw-hidden tw-min-w-0 tw-pr-6 lg:tw-flex' aria-hidden='true' />
        )}
        {/* Col 4: invisible spacer mirrors the waveform column */}
        {showWaveform && <div className='tw-invisible tw-hidden tw-min-w-0 tw-pr-6 lg:tw-flex' aria-hidden='true' />}
        {/* Col 5: invisible spacer mirrors the conditional action buttons */}
        <div className='tw-invisible tw-hidden tw-shrink-0 tw-items-center lg:tw-flex' aria-hidden='true'>
          {onEditPrompt ? <div className='tw-w-10' /> : null}
          {onDownload ? (
            <div className={cn(downloadButtonStyle === "icon-text" ? "tw-w-10 sm:tw-w-28" : "tw-w-10")} />
          ) : null}
        </div>
      </div>
      <div className='tw-border-b-2 tw-border-dotted tw-border-black/10' />

      {/* Rows */}
      {rows}

      {/* Footer CTA */}
      {generateCtaHref && generateFooterTitle && (
        <GenerateFooter
          href={generateCtaHref}
          title={generateFooterTitle}
          subtitle={generateFooterSubtitle}
          onGenerateSfx={onGenerateSfx}
          buttonLabel={tList("generate_footer_button")}
        />
      )}
    </div>
  );
};

CategoryExploreList.displayName = "CategoryExploreList";

export { CategoryExploreList };
export type { SoundEffectExploreItem, CategoryExploreListProps, CategoryExploreListLabels };
